/* eslint-disable max-statements */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./styles.css";
import DreamCarGoogleMap from "../../shared/dream-car-gmap";
import GoogleMapScript from "../../shared/googlemap-script";
import { MAP_OPTIONS, MAP_HEIGHT, HI_MAP_HEIGHT, TAB_LIST } from "./constant";
import InspectionLocationSearchV2 from "../inspection-location-search-v2";
// import LocationSearch from "./location-search";
import AutoComplete from "../../../utils/auto-complete";
import { MAP_TYPE, NUMBER } from "../../../constants/app-constants";
import PlotIcon from "./images/plot-icon.svg";
import { getSlotServicibility } from "../inspection-location-search-v2/selector";
import { sendCustomGaEvents } from "../../../tracking";
import addressValidator from "../../../utils/validators/address-validator-v2";
import { Label } from "@cars24/turbo-web/lib/label";
import { Button } from "@cars24/turbo-web/lib/button";
import { Chips } from "@cars24/turbo-web/lib/chips";
import { LinkButton } from "@cars24/turbo-web/lib/link-button";
import { InputForm } from "@cars24/turbo-web/lib/input-form";
import { BodyText } from "@cars24/turbo-web/lib/body-text";
import { TinyText } from "@cars24/turbo-web/lib/tiny-text";
import { useTokens } from "@cars24/turbo-web/lib/theme-provider";

const LocationPickerWithMapV2 = ({
    isLoading = false,
    allowAutoDetect = false,
    autoDetectedCoords = {},
    handleConfirmLocation = () => { },
    token = "",
    address1,
    landmark,
    fetchSlotsConnect,
    updateGeoDetailsInLeadConnect,
    setAddressConnect,
    setLandmarkConnect,
    fetchLeadDetailsConnect,
    secureToken
}) => {
    const {tokens} = useTokens();
    const mapType = MAP_TYPE.GOOGLE;
    const preFetchedLatLng = Object.keys(autoDetectedCoords).length && allowAutoDetect;
    const [locCoords, setLocCoords] = useState({});
    const [locationName, setLocationName] = useState(null);
    const [showMapView, setShowMapView] = useState(preFetchedLatLng ? true : false);
    const [location, setLocation] = useState({});
    const [inspectionLocation, setInspectionLocation] = useState("");
    const [isLocationSearchEnabled, setIsLocationSearchEnabled] = useState(false);
    const [isHiAvailable, setIsHiAvailable] = useState(false);
    const [locationError, setLocationError] = useState(false);

    useEffect(() => {
        if (!isLoading && !allowAutoDetect) {
            setShowMapView(false);
        }
    }, [isLoading, allowAutoDetect]);

    useEffect(() => {
        if (token) {
            fetchLeadDetailsConnect(token, {}, secureToken);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const isCtaDisabled = () => {
        return !addressValidator(address1) || !inspectionLocation;
    };

    const handleLocationType = (item) => {
        setInspectionLocation(item);
    };

    const onChangeAddress = ({ target: { value } }) => {
        setAddressConnect(value);
    };

    const onChangeLandmark = ({ target: { value } }) => {
        setLandmarkConnect(value);
    };

    const getPincode = (data) => {
        if (!data) return "";
        const elem = data.address_components.filter((item) => item.types.includes("postal_code"));
        if (!elem || !elem[0]) return "";
        return elem[0].short_name || "";
    };

    const onLocationChange = async ({ label, coords, formattedAddress, restrictToggle = false }) => {
        setLocCoords({ ...coords });
        let address = label;

        if (!address) {
            const locationRes = (await AutoComplete.getInstance(mapType).getGeoCode({
                location: coords
            }))[0];

            address = locationRes.formatted_address;
        }
        setLocationName(address);
        setLocation({ ...(!restrictToggle ? { ...coords } : { ...location }), address, formattedAddress });
        setShowMapView(true);
        setAddressConnect("");
        setLandmarkConnect("");
    };

    useEffect(() => {
        if (preFetchedLatLng) {
            onLocationChange({ coords: autoDetectedCoords });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoDetectedCoords, allowAutoDetect]);

    const onCenterChanged = (coords) => {
        onLocationChange({ coords, restrictToggle: true });
    };

    const generateGeoDetailsPayload = (tempLocation, isHiActive = true) => {
        return {
            userLat: tempLocation.lat,
            userLng: tempLocation.lng,
            geoAddress: locationName,
            address1: isHiActive ? address1 || "" : "",
            address2: isHiActive ? landmark || "" : "",
            addressType: (inspectionLocation.title || "").toLowerCase()
        };
    };

    const handleSlots = (tempLocation, coords) => {
        fetchSlotsConnect({
            token,
            ...coords
        }, true, true).then((slotResponse) => {
            setIsHiAvailable(!!slotResponse.zone);
            sendCustomGaEvents({
                event: "slot_availabiliy",
                eventCategory: `location_method_Current"}`,
                eventAction: getSlotServicibility(slotResponse),
                eventLabel: token
            });
            if (!slotResponse.zone) {
                const payload = generateGeoDetailsPayload(tempLocation, false);
                updateGeoDetailsInLeadConnect(payload, token).then(() => {
                    handleConfirmLocation(tempLocation);
                });
            }
        }).catch(() => {
            setIsHiAvailable(false);
            const payload = generateGeoDetailsPayload(tempLocation, false);
            updateGeoDetailsInLeadConnect(payload, token).then(() => {
                handleConfirmLocation(tempLocation);
            });
        });
    };

    const onConfirmLocation = () => {
        const tempLocation = { ...location, ...locCoords };
        const coords = { lat: tempLocation.lat, lng: tempLocation.lng };
        // setPositionConnect(coords);
        if (isHiAvailable) {
            const payload = generateGeoDetailsPayload(tempLocation);
            updateGeoDetailsInLeadConnect(payload, token).then(() => {
                handleConfirmLocation(tempLocation);
            });
        } else {
            handleSlots(tempLocation, coords);
        }
    };

    const handleChange = () => {
        setShowMapView(false);
        setAddressConnect("");
        setLandmarkConnect("");
        setIsHiAvailable(false);
    };

    const hasCoordinates = () => !!(location.lat && location);

    const renderMapView = (height) => {
        if (!hasCoordinates()) return null;
        return (
            <React.Fragment>
                <DreamCarGoogleMap
                    key={`${location.lat}${location.lng}`}
                    lat={location.lat}
                    lng={location.lng}
                    stopZoom={false}
                    height={height}
                    addMarker={false}
                    addCenterListener={true}
                    zoomControl={true}
                    onCenterChanged={onCenterChanged}
                    mapOptions={MAP_OPTIONS}
                    addCustomZoomControlOptions={true}
                />
                <div styleName={"styles.locator"}>
                    <img src={PlotIcon} alt="PlotIcon" />
                </div>
            </React.Fragment>
        );
    };

    const renderToolTip = () => {
        return (
            <div styleName={"styles.errorText"}>
                <TinyText color={tokens.text_default_text_inverse} isBold={true}>Our car expert will come here</TinyText>
                <TinyText color={tokens.text_default_text_disabled}>Adjust the pin accurately on the map</TinyText>
            </div>
        );
    };

    const renderAddress = () => {
        if (location && location.formattedAddress && location.formattedAddress.main_text) {
            return (
                <React.Fragment>
                    <BodyText
                        level={NUMBER.TWO}
                        isBold={true}
                        color={tokens.text_default_text_primary}
                    >
                        {location.formattedAddress.main_text}
                    </BodyText>
                    <BodyText
                        level={NUMBER.THREE}
                        color={tokens.text_default_text_primary}
                    >
                        {location.formattedAddress.secondary_text}
                    </BodyText>
                </React.Fragment>
            );
        }

        return (
            <BodyText
                level={NUMBER.THREE}
                color={tokens.text_default_text_primary}
            >
                {locationName}
            </BodyText>);
    };

    const renderConfirmationCta = (isDisabled) => {
        return (
            <Button
                onClick={onConfirmLocation}
                label={"Confirm Location"}
                loading={isLoading}
                disabled={isDisabled}
                size="large"
            />
        );
    };

    // eslint-disable-next-line complexity
    const renderLayout = () => {
        if (isHiAvailable) {
            return (
                <React.Fragment>
                    <div styleName={"styles.googleMap"}>
                        <div styleName={"styles.googleMapWrapperHomeInspection"}>
                            {renderToolTip()}
                            {renderMapView(HI_MAP_HEIGHT)}
                        </div>
                        <div styleName={"styles.addressChangeCta"}>
                            {renderAddress()}
                            <LinkButton
                                label={"Change"}
                                onClick={handleChange}
                                variant="brand"
                            />
                        </div>
                    </div>
                    <div styleName={"styles.inspectionType"}>
                        <Label level={NUMBER.FOUR} color={tokens.text_default_text_primary}>Inspection at</Label>
                        <div styleName={"styles.tabsWrapper"}>
                            <ul styleName={"styles.tabs"}>
                                {
                                    TAB_LIST.map((item, index) => (
                                        <Chips
                                            key={`location-${index}`}
                                            onClick={() => handleLocationType(item)}
                                            label={item.title}
                                            suffixIcon={<img src={item.icon} alt={index} />}
                                            isActive={inspectionLocation && inspectionLocation.title === item.title}
                                            showSuffixIconOnActive={true}
                                        />
                                    ))
                                }
                            </ul>
                            <InputForm
                                placeholder={inspectionLocation ? inspectionLocation.hiPlaceHolder : ""}
                                onChange={onChangeAddress}
                                value={address1 || ""}
                                validator={addressValidator}
                                maxLength={255}
                                label={""}
                                allowClear={false}
                            />
                            <InputForm
                                placeholder={"Landmark (Optional)"}
                                onChange={onChangeLandmark}
                                value={landmark || ""}
                                validator={addressValidator}
                                maxLength={255}
                                label={""}
                                allowClear={false}
                            />
                        </div>
                    </div>
                    <div styleName={"styles.confirmationCta"}>
                        {renderConfirmationCta(isCtaDisabled())}
                    </div>
                </React.Fragment>
            );
        }
        if (showMapView) {
            return (
                <React.Fragment>
                    <div styleName={"styles.googleMapWrapper"}>
                        {renderToolTip()}
                        {renderMapView(MAP_HEIGHT)}
                    </div>
                    <div styleName={"styles.locationWrapper"}>
                        <div>
                            {renderAddress()}
                        </div>
                        <Button
                            label={"Change"}
                            onClick={handleChange}
                            variant={"secondary"}
                            size={"small"}
                        />
                    </div>
                    {renderConfirmationCta()}
                </React.Fragment>
            );
        }

        return (
            <InspectionLocationSearchV2
                inspectionLocation={inspectionLocation}
                isLocationSearchEnabled={isLocationSearchEnabled}
                isHiAvailable={isHiAvailable}
                setInspectionLocation={setInspectionLocation}
                setIsLocationSearchEnabled={setIsLocationSearchEnabled}
                setIsHiAvailable={setIsHiAvailable}
                onLocationChange={onLocationChange}
                getPincode={getPincode}
                locationError={locationError}
                setLocationError={setLocationError}
                token={token}
            />
        );
    };

    return (
        <React.Fragment>
            <GoogleMapScript />
            <div>
                <Label
                    level={NUMBER.ONE}
                    color={tokens.text_default_text_primary}
                    fontWeight="semibold"
                >
                    Confirm car location
                </Label>
                {renderLayout()}
            </div>
        </React.Fragment>
    );
};

LocationPickerWithMapV2.propTypes = {
    isLoading: PropTypes.bool,
    allowAutoDetect: PropTypes.bool,
    handleConfirmLocation: PropTypes.func,
    autoDetectedCoords: PropTypes.object,
    token: PropTypes.string,
    address1: PropTypes.string,
    landmark: PropTypes.string,
    fetchSlotsConnect: PropTypes.func,
    updateGeoDetailsInLeadConnect: PropTypes.func,
    setAddressConnect: PropTypes.func,
    setLandmarkConnect: PropTypes.func,
    fetchLeadDetailsConnect: PropTypes.func,
    secureToken: PropTypes.string
};

export default LocationPickerWithMapV2;

